import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { ChevronDown, Flame, ChevronLeft, ChevronRight } from 'lucide-react';
import { classicQuestionsApi } from '../services/classicQuestionsApi';
import { Helmet } from 'react-helmet-async';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a1a 0%, #15152a 50%, #0d0d1e 100%); /* Darker, more saturated gradient */
  position: relative;
  overflow: hidden;
`;

const StarField = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0; /* 确保在问题卡片下层 */
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Star = styled.div`
  position: absolute;
  background: #a8dadc; /* Futuristic light blue/cyan */
  border-radius: 50%;
  animation: twinkle ${props => props.duration || '3s'} infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
  box-shadow: 0 0 5px 2px rgba(168, 218, 220, 0.7); /* Add a subtle glow */
  
  @keyframes twinkle {
    0%, 100% { 
      opacity: 0.5; /* Slightly higher base opacity */
      transform: scale(0.8); /* Smaller base size */
    }
    50% { 
      opacity: 1; 
      transform: scale(1.5); /* More pronounced twinkle */
    }
  }
`;

const ShootingStar = styled.div`
  position: absolute;
  width: 4px; /* Make it slightly larger */
  height: 4px;
  background: linear-gradient(45deg, transparent, #84dcc6, transparent); /* Futuristic green/cyan */
  border-radius: 50%;
  animation: shoot ${props => props.duration || '4s'} infinite linear;
  animation-delay: ${props => props.delay || '0s'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 150px; /* Longer tail */
    height: 2px; /* Thicker tail */
    background: linear-gradient(90deg, transparent, rgba(132, 220, 198, 0.9), transparent); /* Match shooting star color with glow */
    box-shadow: 0 0 10px 3px rgba(132, 220, 198, 0.7); /* Add a glow to the tail */
    transform: translateX(-100px);
  }
  
  @keyframes shoot {
    0% {
      transform: translateX(-200px) translateY(-200px) rotate(-45deg); /* Start further back and rotate */
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    90% {
      opacity: 1;
    }
    100% {
      transform: translateX(120vw) translateY(120vh) rotate(-45deg); /* End further out */
      opacity: 0;
    }
  }
`;

const FloatingParticle = styled.div`
  position: absolute;
  width: ${props => props.size || '4px'};
  height: ${props => props.size || '4px'};
  background: rgba(132, 220, 198, 0.2); /* Match shooting star and add transparency */
  border-radius: 50%;
  animation: float ${props => props.duration || '6s'} infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0px) translateX(0px) scale(1);
      opacity: 0.1;
    }
    25% {
      transform: translateY(-30px) translateX(15px) scale(1.2); /* More varied movement and scaling */
      opacity: 0.3;
    }
    50% {
      transform: translateY(-15px) translateX(-20px) scale(0.9); /* More varied movement and scaling */
      opacity: 0.2;
    }
    75% {
      transform: translateY(-40px) translateX(10px) scale(1.1); /* More varied movement and scaling */
      opacity: 0.4;
    }
  }
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(15, 15, 35, 0.9);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 1000;
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 1.5rem;
  color: #ffffff;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const BrandIcon = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 8px;
  transition: all 0.2s ease;
`;

const BrandText = styled.span`
  color: #ffffff;
`;

const DesktopNav = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background: rgba(99, 102, 241, 0.2);
    color: #6366F1;
  }
  
  &.active {
    background: rgba(99, 102, 241, 0.2);
    color: #6366F1;
  }
`;

const UserActions = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileActions = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

/* 占位，移动端登录按钮样式在 LoginButton 定义之后声明 */

const LoginButton = styled.button`
  background: linear-gradient(45deg, #6366F1, #8B5CF6);
  border: none;
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  transition: transform 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    transform: translateY(-1px);
  }
`;

const MobileLoginButton = styled(LoginButton)`
  padding: 8px 14px;
  font-size: 12px;
  border-radius: 20px;
`;

const MainContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 100px 20px 40px 20px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 80px 14px 24px 14px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 16px 0;
  color: #ffffff;
  letter-spacing: -1px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const PageDescription = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
  
  .highlight {
    color: #667eea;
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    max-width: 90%;
  }
`;

const Filters = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 10px;
  
  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  min-width: 120px;
  
  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`;

const SelectButton = styled.button`
  appearance: none;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px 12px 8px 12px;
  font-size: 14px;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  
  &:hover {
    border-color: #667eea;
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    height: 40px;
  }
`;

const SelectIcon = styled.div`
  color: #6c757d;
  transition: transform 0.2s ease;
  font-size: 12px;
  
  &.open {
    transform: rotate(180deg);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  margin-top: 2px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #2c3e50;
  height: 32px;
  
  &:hover {
    background: #f8fafc;
  }
  
  &.selected {
    background: #667eea;
    color: white;
  }
`;

const CheckIcon = styled.div`
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
`;

const QuestionCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  backdrop-filter: blur(10px);
  z-index: 10; /* 确保在星空效果之上 */
  
  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4), 0 8px 20px rgba(0, 0, 0, 0.3);
    transform: translateY(-4px);
    border-color: rgba(102, 126, 234, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 18px;
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const QuestionText = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
  line-height: 1.6;
  letter-spacing: -0.01em;
  flex: 1;
  margin-right: 16px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const InfoButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  height: 28px;
  min-width: fit-content;
`;

const DifficultyFlames = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const FlameIcon = styled(Flame)`
  width: 12px;
  height: 12px;
  transition: all 0.2s ease;
  
  &.easy {
    color: #10b981;
    fill: #10b981;
  }
  
  &.medium {
    color: #fbbf24;
    fill: #fbbf24;
  }
  
  &.hard {
    color: #ef4444;
    fill: #ef4444;
  }
  
  &.inactive {
    color: #e5e7eb;
    fill: #e5e7eb;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const Tag = styled.span`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
  white-space: nowrap;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    background: #edf2f7;
    border-color: #cbd5e0;
    transform: translateY(-1px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #718096;
  font-size: 16px;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  margin: 20px 0;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 40px 0;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const PageButton = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: ${props => props.disabled ? '#f7fafc' : '#ffffff'};
  color: ${props => props.disabled ? '#a0aec0' : '#2d3748'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    border-color: #cbd5e0;
    background: #f7fafc;
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const PageInfo = styled.div`
  color: #4a5568;
  font-size: 16px;
  font-weight: 500;
  margin: 0 8px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;


export default function ClassicQuestionsPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [questionsData, setQuestionsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const data = await classicQuestionsApi.listQuestions({ limit: 500 });
        setQuestionsData(data);
      } catch (error) {
        console.error("Failed to fetch questions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(questionsData.map(q => q.category).filter(Boolean))];
    return cats.sort();
  }, [questionsData]);

  const allQuestions = useMemo(() => {
    if (!category) return questionsData;
    return questionsData.filter(q => q.category === category);
  }, [category, questionsData]);

  // 分页逻辑
  const totalPages = Math.ceil(allQuestions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const list = allQuestions.slice(startIndex, endIndex);

  // 重置到第一页当类别改变时
  useEffect(() => {
    setCurrentPage(1);
  }, [category]);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.select-wrapper')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleQuestionClick = (questionId) => {
    navigate(`/app/questions/${questionId}`);
  };

  const handleCategorySelect = (selectedCategory) => {
    setCategory(selectedCategory);
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getDisplayText = () => {
    return category === '' ? '全部类别' : category;
  };


  const handleHomeClick = () => {
    navigate('/app');
  };

  const handleLogoClick = () => {
    if (isAuthenticated) {
      navigate('/app');
    } else {
      navigate('/');
    }
  };

  const renderDifficultyFlames = (difficulty) => {
    if (!difficulty) return null;

    const difficultyConfig = {
      easy: { count: 1, class: 'easy' },
      medium: { count: 2, class: 'medium' },
      hard: { count: 3, class: 'hard' }
    };

    const config = difficultyConfig[difficulty] || difficultyConfig.medium;

    return (
      <DifficultyFlames>
        {[1, 2, 3].map((flame) => (
          <FlameIcon
            key={flame}
            className={flame <= config.count ? config.class : 'inactive'}
          />
        ))}
      </DifficultyFlames>
    );
  };

  // 生成结构化数据
  const generateStructuredData = () => {
    const questions = allQuestions;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "经典面试题库",
      "description": "专业面试官精心筛选的经典面试问题，涵盖工作态度、薪资谈判、履历追问、自我认知、动机了解、行业认知等多个类别，助你掌握面试核心技巧",
      "url": "https://zhimai.ai/classic-questions",
      "numberOfItems": questions.length,
      "itemListElement": questions.slice(0, 20).map((question, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Question",
          "name": question.question,
          "text": question.question,
          "category": question.category || undefined,
          "difficulty": question.difficulty || undefined,
          "keywords": question.tags && Array.isArray(question.tags) 
            ? question.tags.join(", ") 
            : (question.tags || ""),
          "answer": {
            "@type": "Answer",
            "text": question.guidance || question.sample_answer || ""
          }
        }
      }))
    };
  };

  return (
    <PageContainer>
      <Helmet>
        <title>经典面试题库 - 专业面试问题大全 | 职唛AI</title>
        <meta name="description" content="专业面试官精心筛选的经典面试问题，涵盖工作态度、薪资谈判、履历追问、自我认知、动机了解、行业认知等多个类别。每个问题都包含详细解析和参考答案，助你掌握面试核心技巧，提升面试成功率。" />
        <meta name="keywords" content="面试题库,经典面试题,面试问题,面试技巧,面试准备,工作态度,薪资谈判,履历追问,自我认知,动机了解,行业认知,面试答案,面试解析" />
        <meta name="author" content="职唛AI" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://zhimai.ai/classic-questions" />
        <meta property="og:title" content="经典面试题库 - 专业面试问题大全 | 职唛AI" />
        <meta property="og:description" content="专业面试官精心筛选的经典面试问题，涵盖多个类别，每个问题都包含详细解析和参考答案，助你掌握面试核心技巧。" />
        <meta property="og:image" content="https://zhimai.ai/images/classic-questions-og.jpg" />
        <meta property="og:site_name" content="职唛AI" />
        <meta property="og:locale" content="zh_CN" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://zhimai.ai/classic-questions" />
        <meta property="twitter:title" content="经典面试题库 - 专业面试问题大全 | 职唛AI" />
        <meta property="twitter:description" content="专业面试官精心筛选的经典面试问题，涵盖多个类别，每个问题都包含详细解析和参考答案，助你掌握面试核心技巧。" />
        <meta property="twitter:image" content="https://zhimai.ai/images/classic-questions-og.jpg" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://zhimai.ai/classic-questions" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(generateStructuredData())}
        </script>
      </Helmet>

      <StarField>
        {/* 闪烁的星星 */}
        {Array.from({ length: 50 }, (_, i) => (
          <Star
            key={`star-${i}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
            }}
            duration={`${Math.random() * 3 + 2}s`}
            delay={`${Math.random() * 2}s`}
          />
        ))}
        
        {/* 流星 */}
        {Array.from({ length: 5 }, (_, i) => (
          <ShootingStar
            key={`shooting-${i}`}
            style={{
              left: `${Math.random() * 120 - 10}%`, // 允许超出屏幕范围
              top: `${Math.random() * 120 - 10}%`,
              transform: `rotate(${Math.random() * 360}deg)`, // 随机旋转角度
            }}
            duration={`${Math.random() * 4 + 3}s`} // 3-7秒的随机持续时间
            delay={`${Math.random() * 8}s`} // 0-8秒的随机延迟
          />
        ))}
        
        {/* 浮动粒子 */}
        {Array.from({ length: 20 }, (_, i) => (
          <FloatingParticle
            key={`particle-${i}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            size={`${Math.random() * 4 + 2}px`}
            duration={`${Math.random() * 4 + 4}s`}
            delay={`${Math.random() * 3}s`}
          />
        ))}
      </StarField>

      <Header>
        <NavContent>
          <LeftGroup>
            <Brand onClick={handleLogoClick}>
              <BrandIcon src="/images/logos/logo.svg" alt="职唛 AI Logo" />
              <BrandText>职唛 AI</BrandText>
            </Brand>
          </LeftGroup>

          <DesktopNav>
            <NavItem as="a" href="/resume">
              <span>简历诊断</span>
            </NavItem>
            <NavItem as="a" href="/app/interviews/prep">
              <span>面试押题</span>
            </NavItem>
            <NavItem as="a" href="/mock-interview">
              <span>模拟面试</span>
            </NavItem>
            <NavItem as="a" href="/app/meetings">
              <span>会议助手</span>
            </NavItem>
          </DesktopNav>

          <UserActions>
            {isAuthenticated ? (
              <LoginButton onClick={handleHomeClick}>
                主页
              </LoginButton>
            ) : (
              <LoginButton onClick={() => navigate('/login')}>
                登录
              </LoginButton>
            )}
          </UserActions>

          <MobileActions>
            {isAuthenticated ? (
              <MobileLoginButton onClick={handleHomeClick}>主页</MobileLoginButton>
            ) : (
              <MobileLoginButton onClick={() => navigate('/login')}>登录</MobileLoginButton>
            )}
          </MobileActions>
        </NavContent>
      </Header>

      <MainContent>
        <PageHeader>
          <PageTitle>经典题库</PageTitle>
          <PageDescription>
            专业面试官精心筛选，助你掌握面试<span className="highlight">核心技巧</span>
          </PageDescription>
          
          {/* SEO优化的静态内容 */}
          <div style={{ display: 'none' }}>
            <h1>经典面试题库 - 专业面试问题大全</h1>
            <h2>面试问题分类</h2>
            <p>我们的经典面试题库包含以下主要类别：</p>
            <ul>
              <li>工作态度类面试问题 - 考察候选人的工作态度、责任心和职业素养</li>
              <li>薪资谈判类面试问题 - 测试候选人的谈判技巧和期望管理能力</li>
              <li>履历追问类面试问题 - 深入了解候选人的工作经历和职业发展</li>
              <li>自我认知类面试问题 - 评估候选人的自我反思和认知能力</li>
              <li>动机了解类面试问题 - 了解候选人的求职动机和职业规划</li>
              <li>行业认知类面试问题 - 测试候选人对行业和公司的了解程度</li>
            </ul>
            
            <h2>面试技巧指导</h2>
            <p>每个面试问题都包含：</p>
            <ul>
              <li>详细的题目解析 - 帮助理解面试官的考察意图</li>
              <li>标准参考答案 - 提供专业的回答思路和示例</li>
              <li>难度等级标识 - 根据问题难度进行分类</li>
              <li>相关标签分类 - 便于快速定位相关问题</li>
            </ul>
            
            <h2>面试准备建议</h2>
            <p>使用我们的经典题库进行面试准备：</p>
            <ul>
              <li>按类别系统学习 - 全面覆盖各类面试问题</li>
              <li>模拟面试练习 - 提高实际面试表现</li>
              <li>答案思路整理 - 形成自己的回答框架</li>
              <li>持续更新学习 - 跟上最新的面试趋势</li>
            </ul>
            
            <h2>常见面试问题示例</h2>
            <p>经典面试问题包括但不限于：</p>
            <ul>
              <li>"实际工作中，当领导质疑你的工作态度时，你是怎么回应的？"</li>
              <li>"你的整体能力还是不错的，如果薪资没达到你的心理预期，你会怎么权衡？"</li>
              <li>"看到你简历中有一段空窗期，为什么你离职间隔期这么长？"</li>
              <li>"你觉得自己身上最让你头疼的那个短板是什么？能不能说个具体点的？"</li>
              <li>"你上一份工作怎么突然就不干了？说说真实原因吧。"</li>
            </ul>
          </div>
        </PageHeader>

        <Filters>
          <SelectWrapper className="select-wrapper">
            <SelectButton onClick={toggleDropdown}>
              <span>{getDisplayText()}</span>
              <SelectIcon className={isDropdownOpen ? 'open' : ''}>
                <ChevronDown size={16} />
              </SelectIcon>
            </SelectButton>
            
            {isDropdownOpen && (
              <DropdownMenu>
                <DropdownItem 
                  className={category === '' ? 'selected' : ''}
                  onClick={() => handleCategorySelect('')}
                >
                  <CheckIcon>{category === '' ? '✓' : ''}</CheckIcon>
                  全部类别
                </DropdownItem>
                {categories.map(c => (
                  <DropdownItem 
                    key={c}
                    className={category === c ? 'selected' : ''}
                    onClick={() => handleCategorySelect(c)}
                  >
                    <CheckIcon>{category === c ? '✓' : ''}</CheckIcon>
                    {c}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            )}
          </SelectWrapper>
        </Filters>


        <div>
          {list.map(item => (
            <QuestionCard key={item.id} onClick={() => handleQuestionClick(item.id)}>
              <QuestionHeader>
                <QuestionText>{item.question}</QuestionText>
                <RightSection>
                  {item.category && (
                    <InfoButton>
                      <span>{item.category}</span>
                    </InfoButton>
                  )}
                  <InfoButton>
                    {renderDifficultyFlames(item.difficulty)}
                  </InfoButton>
                </RightSection>
              </QuestionHeader>
              <TagsContainer>
                {Array.isArray(item.tags) && item.tags
                  .filter(tag => tag !== item.category)
                  .map((t, i) => (
                    <Tag key={i}>{t}</Tag>
                  ))}
              </TagsContainer>
            </QuestionCard>
          ))}
          {list.length === 0 && <EmptyState>暂无题目</EmptyState>}
        </div>

        {/* 分页组件 */}
        {allQuestions.length > itemsPerPage && (
          <PaginationContainer>
            <PageButton 
              onClick={handlePreviousPage} 
              disabled={currentPage === 1}
              title="上一页"
            >
              <ChevronLeft size={20} />
            </PageButton>
            <PageInfo>
              第 {currentPage} 页 / 共 {totalPages} 页
            </PageInfo>
            <PageButton 
              onClick={handleNextPage} 
              disabled={currentPage === totalPages}
              title="下一页"
            >
              <ChevronRight size={20} />
            </PageButton>
          </PaginationContainer>
        )}
      </MainContent>
    </PageContainer>
  );
}
